// @ts-strict-ignore
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { DevTools } from 'jotai-devtools'
import CacheBuster from 'react-cache-buster'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { usePageViewTracker } from '@node-space/hooks'
import { RowsSkeletonLoader } from '@node-space/ui-components'
import { AppProviders } from 'components/app/AppProviders'
import { ProfileSettingProtectedPage } from 'components/app/ProfileSettingProtectedPage'
import CapabilityProtectedComponent from 'components/CapabilityProtectedComponent'
import ErrorContent from 'components/ErrorContent'
import { PendingAgreements } from 'components/PendingAgreements/PendingAgreements'
import ProtectedComponent from 'components/ProtectedComponent'
import { PathNames } from 'constants/General'
import { AccountsData } from 'contexts/accountsContext'
import { useAccountCapabilities } from 'hooks/capabilities/useAccountCapabilities'
import useSentryContext from 'hooks/sentry/useSentryContext'
import useSentryIdentification from 'hooks/sentry/useSentryIdentification'
import { useBffHotReload } from 'hooks/useBffHotReload'
import { useComplianceViewHelper } from 'hooks/useComplianceViewHelper'
import { useCustomerModel } from 'hooks/useCustomerModel'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useFingerprint } from 'hooks/useFingerprint'
import useLaunchDarkly from 'hooks/useLaunchDarkly'
import { useRequestsOnLogin } from 'hooks/useRequestsOnLogin'
import AccountError from 'pages/AccountError'
import Approvals from 'pages/Approvals/Approvals'
import { ApplicationOnboarding } from 'pages/Authenticate/ApplicationOnboarding'
import { ApplicationUnsuccessful } from 'pages/Authenticate/ApplicationUnsuccessful'
import { Authenticate } from 'pages/Authenticate/Authenticate'
import { Billing } from 'pages/Billing/Billing'
import Channels from 'pages/Channels/Channels'
import { CommunicationPreferences } from 'pages/CommunicationPreferences/CommunicationPreferences'
import { ContactCases } from 'pages/ContactCases/ContactCases'
import { ContactsMFE } from 'pages/Contacts/ContactsMFE'
import { useContactPageBreadcrumbs } from 'pages/Contacts/hooks/useContactPageBreadcrumbs'
import { CustomerPendingAgreements } from 'pages/CustomerPendingAgreements/CustomerPendingAgreements'
import { Customer } from 'pages/Customers/Customer'
import { CustomerFeeSetup } from 'pages/Customers/CustomerFeeSetup'
import { Customers } from 'pages/Customers/Customers'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { CustomersV2 } from 'pages/CustomersV2/CustomersV2'
import { LiveCustomers } from 'pages/CustomersV2/LiveCustomers/LiveCustomers'
import DevXGetStarted from 'pages/Dashboard/components/DevXGetStarted'
import { Earn } from 'pages/Earn/Earn'
import { EarnDetails } from 'pages/Earn/EarnDetails'
import { useEarnDetailsBreadcrumbs } from 'pages/Earn/hooks/useEarnDetailsBreadcrumbs'
import { GetStarted } from 'pages/GetStarted/GetStarted'
import { HppSettings } from 'pages/HppSettings/HppSettings'
import Impersonate from 'pages/Impersonate'
import { LegalAgreements } from 'pages/LegalAgreements/LegalAgreements'
import ForgotPassword from 'pages/Login/ForgotPassword'
import LoginNew from 'pages/Login/Login'
import PasswordReset from 'pages/Login/PasswordReset'
import { ManageAccount } from 'pages/ManageAccount/ManageAccount'
import ManageChannels from 'pages/ManageChannels/ManageChannels'
import { Integrations } from 'pages/ManageIntegrations/Integrations'
import { MassCustomerLimits } from 'pages/MassCustomerLimits/MassCustomerLimits'
import { MassPayouts } from 'pages/MassPayouts/MassPayouts'
import { MassPayoutReview } from 'pages/MassPayouts/pages/MassPayoutReview'
import { MerchantsDetails } from 'pages/MerchantDetails/MerchantsDetails'
import useCustomerPageBreadcrumbs from 'pages/NestedCustomers/hooks/useCustomerPageBreadcrumbs'
import { NestedCustomerWallet } from 'pages/NestedCustomers/NestedCustomerWallet'
import NotFound from 'pages/NotFound'
import { PaymentLimits } from 'pages/PaymentLimits/PaymentLimits'
import { CryptoPayments } from 'pages/Payments/CryptoPayments'
import { Payments } from 'pages/Payments/Payments'
import { PaymentSupport } from 'pages/PaymentSupport/PaymentSupport'
import { useRewardsDetailsBreadcrumbs } from 'pages/Rewards/hooks/useRewardsDetailsBreadcrumbs'
import { Rewards } from 'pages/Rewards/Rewards'
import { RewardsDetails } from 'pages/Rewards/RewardsDetails'
import { CTAGetStarted } from 'pages/Signup/CTAGetStarted'
import TeamMembers from 'pages/TeamMembers/TeamMembers'
import { TransactionHistory } from 'pages/TransactionHistory/TransactionHistory'
import VerificationStatus from 'pages/Verification/VerificationStatus'
import Verify from 'pages/Verify/Verify'
import { WalletDetails } from 'pages/WalletDetails/WalletDetails'
import Wallets from 'pages/Wallets/Wallets'
import { CountryCodes, Profile } from 'types/types'
import { AmplitudeEvent } from 'utils/amplitude/amplitudeEvents'
import { isLocal, isProduction, isSandbox, isTest } from 'utils/environment'
import { GoogleAnalytics } from 'utils/googleAnalytics'
import { usePardotTracking } from 'utils/pardot/usePardotTracking'
import track from 'utils/tracker'
import { version } from '../package.json'
import PageLayout, { PageLayoutProps } from './components/layout/PageLayout'
import Logout from './components/Logout'

const UserDetailsPage = lazy(() => import('./pages/UserDetails'))

if (isLocal) {
  require('jotai-devtools/styles.css')
}

const App = () => {
  const { t } = useTranslation()

  const [sentryTraceId, setSentryTraceId] = useState('')
  const {
    enableBilling,
    enableHppSettingsPage,
    enableLegalAgreements,
    enablePaymentLimitsManagement,
    enableUsLaunchRestrictions,
    enableConsolidatedPayments,
    enableCustomerFeeSetup,
    enableRewards,
    enableEarn,
    enableEpcV2CustomerCreation,
    enableEpcV2RelianceCustomerCreation,
    enableHideCustomersV1,
  } = useFeatureFlags()
  const location = useLocation()

  const {
    isLoadingRequestsOnLogin,
    profile,
    isVerifiedBusinessAccount,
    isDeveloperAccount,
    isAuthorizedAccount,
    verificationRequestStatus,
    accounts,
    currentAccount,
  } = useRequestsOnLogin()

  const profileContext: { data: Profile; isLoading: boolean } = useMemo(
    () => ({ data: profile, isLoading: isLoadingRequestsOnLogin }),
    [profile, isLoadingRequestsOnLogin]
  )

  const { accountCapabilities, isFetchingAccountCapabilities } = useAccountCapabilities(
    currentAccount?.reference,
    profileContext
  )

  const { isCustomersEnabled, customerModel } = useCustomerModel(accountCapabilities)

  const embeddedEnabled = enableEpcV2CustomerCreation && customerModel === CustomerModels.EMBEDDED
  const relianceEnabled =
    enableEpcV2RelianceCustomerCreation && customerModel === CustomerModels.RELIANCE
  const isEpcV2CustomerCreationEnabled = embeddedEnabled || relianceEnabled

  const customerPageBreadcrumbs = useCustomerPageBreadcrumbs()
  const contactPageBreadcrumbs = useContactPageBreadcrumbs()
  const rewardsDetailsBreadcrumbs = useRewardsDetailsBreadcrumbs()
  const earnDetailsBreadcrumbs = useEarnDetailsBreadcrumbs()

  const accountsContext: { data: AccountsData; isLoading: boolean } = useMemo(
    () => ({
      data: {
        accounts,
        currentAccount,
        isVerifiedBusinessAccount,
        isDeveloperAccount,
        accountCapabilities,
      },
      isLoading: isLoadingRequestsOnLogin,
    }),
    [
      accounts,
      currentAccount,
      isVerifiedBusinessAccount,
      isDeveloperAccount,
      isLoadingRequestsOnLogin,
      accountCapabilities,
    ]
  )

  const isComplianceView = useComplianceViewHelper(profile)

  useBffHotReload()
  useLaunchDarkly(profile, accountsContext)
  useSentryIdentification(profile)
  useSentryContext(currentAccount)
  usePardotTracking()
  usePageViewTracker(GoogleAnalytics)
  const { trackFingerprint } = useFingerprint()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    if (profile?.loggedIn && currentAccount) {
      trackFingerprint({
        merchantName: currentAccount?.name,
        accountId: currentAccount?.id,
        accountReference: currentAccount?.reference,
        profileId: profile?.id,
      })

      // Google Analytics: Tracking start
      track.GA.initialize()
      track.GA.event({
        category: 'Login',
        action: `A user with ID: ${profile?.id} logged in`,
      })

      // HotJar: Tracking start
      track.Hotjar.identify(profile?.id, {
        first_name: profile?.firstName,
        last_name: profile?.lastName,
        id: profile?.id,
        country: profile?.country?.name,
        default_currency: profile?.currencyCode,
        twoFactorEnabled: profile?.twoFactorEnabled,
        registrationAccountType: profile?.options?.registrationAccountType,
      })
      track.Hotjar.event('app_start')
      track.Hotjar.event('user_logged_in')

      // On Sandbox only - and DevX account then log
      if ((isSandbox || isTest) && isDeveloperAccount) {
        track.Amp.track(AmplitudeEvent.DEVX_SANDBOX_DEVELOPER_LOGGED_IN)
      }

      // Amplitude Tracking start
      track.Amp.set('email', profile?.emailAddress)
      track.Amp.set('first_name', profile?.firstName)
      track.Amp.set('last_name', profile?.lastName)
      track.Amp.set('country', profile?.country?.code)
      track.Amp.set('currency', profile?.currencyCode)
      track.Amp.set('account_name', currentAccount.name)
      track.Amp.set('theme', localStorage.getItem('darkMode') === 'true' ? 'dark' : 'light')
      track.Amp.set(
        'system_preference',
        window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      )
      track.Amp.identify({
        user_id: `${profile?.id}`?.padStart(5, '0'),
      })
    }
  }, [profile, isDeveloperAccount, currentAccount, trackFingerprint])

  const shouldShowUKInfoBanner = useMemo(() => {
    return profile?.country?.code === CountryCodes.UK
  }, [profile?.country?.code])

  const canRouteDefault = isAuthorizedAccount

  const defaultPageLayout: PageLayoutProps = {
    loading: isLoadingRequestsOnLogin,
    canRoute: canRouteDefault,
    verificationRequestStatus,
  }

  const onBeforeErrorCaptureHandler = (scope: Sentry.Scope) => {
    const { traceId } = scope.getPropagationContext()
    setSentryTraceId(traceId)
  }

  const cacheBusterEnabled = useMemo(() => {
    return isProduction || isSandbox || isTest
  }, [])

  return (
    <CacheBuster
      currentVersion={version}
      isEnabled={cacheBusterEnabled}
      isVerboseMode={false}
      onCacheClear={(refreshCacheAndReload: () => void) => refreshCacheAndReload()}
    >
      <Sentry.ErrorBoundary
        beforeCapture={onBeforeErrorCaptureHandler}
        fallback={({ componentStack }) => (
          <ErrorContent errorInfo={componentStack} sentryTraceId={sentryTraceId} />
        )}
        onReset={() => {
          setSentryTraceId('')
        }}
      >
        <AppProviders
          profile={profileContext}
          accounts={accountsContext}
          shouldShowUKInfoBanner={shouldShowUKInfoBanner}
        >
          <Routes>
            {/* WALLETS START */}
            <Route element={<PageLayout {...defaultPageLayout} />}>
              <Route
                path={PathNames.WALLETS}
                element={
                  <ProtectedComponent featureName="wallets">
                    <Wallets />
                  </ProtectedComponent>
                }
              />
              <Route
                path={`${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`}
                element={
                  <ProtectedComponent featureName="wallets">
                    <Wallets />
                  </ProtectedComponent>
                }
              />
              <Route
                path={`${PathNames.WALLETS}${PathNames.WALLET_CREATE}`}
                element={
                  <ProtectedComponent featureName="wallets">
                    <Wallets />
                  </ProtectedComponent>
                }
              />
              <Route
                path={`${PathNames.WALLETS}${PathNames.WALLETS_CATCH_ALL}`}
                element={<Wallets />}
              />
              <Route path={`${PathNames.WALLETS}`} element={<Wallets />} />
            </Route>
            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  breadcrumbConfig={[
                    {
                      title: 'wallets.title',
                      link: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
                    },
                    {
                      title: 'wallets.walletDetails',
                    },
                  ]}
                />
              }
            >
              <Route
                path={`${PathNames.WALLETS}${PathNames.WALLETS_DETAILS}`}
                element={<WalletDetails />}
              />
            </Route>
            {/* WALLETS END */}
            {/* PAYMENTS START */}

            {enableConsolidatedPayments && (
              <Route
                element={
                  <PageLayout
                    {...defaultPageLayout}
                    breadcrumbConfig={[{ title: 'sidebar.payments' }]}
                  />
                }
              >
                <Route
                  path={PathNames.PAYMENTS}
                  element={
                    <ProtectedComponent featureName="payments">
                      <Payments />
                    </ProtectedComponent>
                  }
                />
              </Route>
            )}
            <Route element={<PageLayout {...defaultPageLayout} />}>
              <Route
                path={`${PathNames.PAYMENTS}${PathNames.CRYPTO}`}
                element={
                  <ProtectedComponent featureName="payments">
                    <CryptoPayments />
                  </ProtectedComponent>
                }
              />
              {!enableUsLaunchRestrictions && (
                <Route
                  path={`${PathNames.PAYMENTS}${PathNames.CHANNEL}`}
                  element={
                    <ProtectedComponent featureName="payments">
                      <Channels />
                    </ProtectedComponent>
                  }
                />
              )}
            </Route>

            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  breadcrumbConfig={[
                    {
                      title: 'contacts.missingInformation',
                    },
                  ]}
                />
              }
            >
              <Route
                path={PathNames.MISSING_INFORMATION}
                element={
                  <ProtectedComponent featureName="payments">
                    <ContactCases />
                  </ProtectedComponent>
                }
              />
              {/* keeping the /missing-contacts route for retro compatibility in case users have it saved */}
              <Route
                path={PathNames.MISSING_CONTACTS}
                element={<Navigate to={PathNames.MISSING_INFORMATION} replace />}
              />
            </Route>

            {/* PAYMENTS END */}

            {/* MASS PAYOUTS */}
            <Route element={<PageLayout {...defaultPageLayout} canRoute />}>
              <Route
                key="mass-payouts"
                path={PathNames.MASS_PAYOUTS}
                element={
                  <ProtectedComponent featureName="payments">
                    <MassPayouts />
                  </ProtectedComponent>
                }
              />
            </Route>

            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  breadcrumbConfig={[
                    {
                      title: 'massPayouts.massPayouts',
                      link: `${PathNames.MASS_PAYOUTS}`,
                    },
                    {
                      title: 'massPayouts.payoutDetails',
                    },
                  ]}
                />
              }
            >
              <Route
                path={`${PathNames.MASS_PAYOUTS}${PathNames.MASS_PAYOUTS_REVIEW}`}
                element={<MassPayoutReview />}
              />
            </Route>

            {/* Customers */}
            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  canRoute
                  loading={isFetchingAccountCapabilities}
                  breadcrumbConfig={[
                    {
                      title: 'customers.title',
                      link: `${PathNames.CUSTOMERS}`,
                    },
                    ...(isCustomersEnabled ? customerPageBreadcrumbs : []),
                    ...(location.pathname === PathNames.CUSTOMER_FEE_SETUP && enableCustomerFeeSetup
                      ? [
                          {
                            title: 'customers.feeSetup.title',
                            link: PathNames.CUSTOMER_FEE_SETUP,
                          },
                        ]
                      : []),
                  ]}
                />
              }
            >
              <Route
                key="my-customers"
                path={PathNames.CUSTOMERS}
                element={
                  enableHideCustomersV1 ? (
                    <Navigate to={PathNames.CUSTOMERS_IN_FLIGHT} replace />
                  ) : (
                    <ProtectedComponent featureName="customers">
                      <Customers />
                    </ProtectedComponent>
                  )
                }
              />
              <Route
                key="customer-fee-setup"
                path={PathNames.CUSTOMER_FEE_SETUP}
                element={
                  isCustomersEnabled && enableCustomerFeeSetup ? (
                    <ProtectedComponent featureName="customers">
                      <CustomerFeeSetup />
                    </ProtectedComponent>
                  ) : (
                    <></>
                  )
                }
              />
              <Route
                path={`${PathNames.CUSTOMER}`}
                element={
                  isCustomersEnabled ? (
                    <ProtectedComponent featureName="customers">
                      <Customer />
                    </ProtectedComponent>
                  ) : (
                    <></>
                  )
                }
                index
              />
              <Route
                path={`${PathNames.CUSTOMER_WALLET}`}
                element={
                  isCustomersEnabled ? (
                    <ProtectedComponent featureName="customers">
                      <NestedCustomerWallet />
                    </ProtectedComponent>
                  ) : (
                    <></>
                  )
                }
                index
              />
            </Route>

            {/* Customers V2 */}
            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  canRoute
                  loading={isFetchingAccountCapabilities}
                  breadcrumbConfig={[
                    {
                      title: enableHideCustomersV1 ? 'customers.sidebarTitle' : 'customers.titleV2',
                      link: PathNames.CUSTOMERS_IN_FLIGHT,
                    },
                    {
                      title: 'inFlight',
                    },
                  ]}
                />
              }
            >
              <Route
                key="my-customers-in-flight"
                path={PathNames.CUSTOMERS_IN_FLIGHT}
                element={
                  isEpcV2CustomerCreationEnabled ? (
                    <ProtectedComponent featureName="customers">
                      <CustomersV2 />
                    </ProtectedComponent>
                  ) : (
                    <Navigate to={PathNames.CUSTOMERS} replace />
                  )
                }
              />
            </Route>

            {/* Live Customers */}
            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  canRoute
                  loading={isFetchingAccountCapabilities}
                  breadcrumbConfig={[
                    {
                      title: enableHideCustomersV1
                        ? 'customers.sidebarTitle'
                        : 'customers.sidebarTitleV2',
                      link: PathNames.LIVE_CUSTOMERS,
                    },
                    {
                      title: 'live',
                    },
                  ]}
                />
              }
            >
              <Route
                key="live-customers"
                path={PathNames.LIVE_CUSTOMERS}
                element={
                  isEpcV2CustomerCreationEnabled ? (
                    <ProtectedComponent featureName="customers">
                      <LiveCustomers />
                    </ProtectedComponent>
                  ) : (
                    <Navigate to={PathNames.CUSTOMERS} replace />
                  )
                }
              />
            </Route>

            {/* Live Customer Detail */}
            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  canRoute
                  loading={isFetchingAccountCapabilities}
                  breadcrumbConfig={[
                    {
                      title: enableHideCustomersV1
                        ? 'customers.sidebarTitle'
                        : 'customers.sidebarTitleV2',
                      link: PathNames.LIVE_CUSTOMERS,
                    },
                    ...customerPageBreadcrumbs,
                    ...(location.pathname === PathNames.LIVE_CUSTOMER_FEE_SETUP &&
                    enableCustomerFeeSetup
                      ? [
                          {
                            title: 'customers.feeSetup.title',
                            link: PathNames.LIVE_CUSTOMER_FEE_SETUP,
                          },
                        ]
                      : []),
                  ]}
                />
              }
            >
              <Route
                key="live-customer"
                path={PathNames.LIVE_CUSTOMER}
                element={
                  <ProtectedComponent featureName="customers">
                    <Customer />
                  </ProtectedComponent>
                }
              />
              <Route
                key="live-customer-fee-setup"
                path={PathNames.LIVE_CUSTOMER_FEE_SETUP}
                element={
                  enableCustomerFeeSetup ? (
                    <ProtectedComponent featureName="customers">
                      <CustomerFeeSetup />
                    </ProtectedComponent>
                  ) : (
                    <></>
                  )
                }
              />
            </Route>

            {/* Mass Customer Limits */}
            {enablePaymentLimitsManagement && (
              <Route
                element={
                  <PageLayout
                    {...defaultPageLayout}
                    canRoute
                    breadcrumbConfig={[
                      {
                        title: enableHideCustomersV1
                          ? 'customers.sidebarTitle'
                          : 'customers.titleV2',
                        link: PathNames.CUSTOMERS_IN_FLIGHT,
                      },
                      {
                        title: 'massCustomerLimits.title',
                      },
                    ]}
                  />
                }
              >
                <Route
                  key="mass-customer-limits"
                  path={PathNames.MASS_CUSTOMER_LIMITS}
                  element={
                    <ProtectedComponent featureName="paymentLimits">
                      <MassCustomerLimits />
                    </ProtectedComponent>
                  }
                />
              </Route>
            )}

            {/* CONTACTS */}

            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  canRoute
                  breadcrumbConfig={[
                    {
                      title: 'contacts.title',
                      link: PathNames.CONTACTS,
                    },
                    ...contactPageBreadcrumbs,
                  ]}
                />
              }
            >
              <Route
                key="contacts-mfe"
                path={`${PathNames.CONTACTS}/*`}
                element={
                  <ProtectedComponent featureName="contacts">
                    <ContactsMFE />
                  </ProtectedComponent>
                }
              />
            </Route>

            <Route element={<PageLayout {...defaultPageLayout} />}>
              <Route
                key="transaction-history"
                path={PathNames.TRANSACTION_HISTORY}
                element={
                  <ProtectedComponent featureName="wallets">
                    <TransactionHistory />
                  </ProtectedComponent>
                }
              />
            </Route>
            {!isProduction && (
              <Route
                key="dev-get-started"
                element={
                  <PageLayout
                    {...defaultPageLayout}
                    title={t('home')}
                    isTitleEnabled={false}
                    canRoute
                  />
                }
              >
                <Route path={PathNames.DEV_GET_STARTED} element={<DevXGetStarted />} />,
              </Route>
            )}
            {/* APPROVALS */}
            <Route element={<PageLayout {...defaultPageLayout} canRoute />}>
              <Route
                path={PathNames.APPROVALS}
                element={
                  <ProtectedComponent featureName="manageApprovals">
                    <Approvals />
                  </ProtectedComponent>
                }
              />
            </Route>

            {/* INTEGRATIONS */}
            <Route element={<PageLayout {...defaultPageLayout} canRoute />}>
              <Route
                key="integrations"
                path={PathNames.INTEGRATIONS}
                element={
                  <ProtectedComponent featureName="integrations">
                    <Integrations />
                  </ProtectedComponent>
                }
              />
            </Route>

            {/* REWARDS */}
            {enableRewards && !enableEarn && (
              <Route
                element={
                  <PageLayout
                    {...defaultPageLayout}
                    loading={isLoadingRequestsOnLogin || isFetchingAccountCapabilities}
                    canRoute
                    breadcrumbConfig={rewardsDetailsBreadcrumbs}
                  />
                }
              >
                <Route
                  key="rewards"
                  path={PathNames.REWARDS}
                  element={
                    <ProtectedComponent featureName="rewards">
                      <CapabilityProtectedComponent
                        capabilityCheck={capabilities =>
                          capabilities?.isEarningRewardsCapabilityEnabled
                        }
                      >
                        <Rewards />
                      </CapabilityProtectedComponent>
                    </ProtectedComponent>
                  }
                />
                <Route
                  key="rewards-details"
                  path={PathNames.REWARDS_DETAILS}
                  element={
                    <ProtectedComponent featureName="rewards">
                      <CapabilityProtectedComponent
                        capabilityCheck={capabilities =>
                          capabilities?.isEarningRewardsCapabilityEnabled
                        }
                      >
                        <RewardsDetails />
                      </CapabilityProtectedComponent>
                    </ProtectedComponent>
                  }
                />
              </Route>
            )}

            {/* EARN */}
            {enableEarn && (
              <Route
                element={
                  <PageLayout
                    {...defaultPageLayout}
                    canRoute
                    breadcrumbConfig={earnDetailsBreadcrumbs}
                  />
                }
              >
                <Route
                  key="earn"
                  path={PathNames.EARN}
                  element={
                    <ProtectedComponent featureName="rewards">
                      <CapabilityProtectedComponent
                        capabilityCheck={capabilities =>
                          capabilities?.isEarningRewardsCapabilityEnabled
                        }
                      >
                        <Earn />
                      </CapabilityProtectedComponent>
                    </ProtectedComponent>
                  }
                />
                <Route
                  key="earn-details"
                  path={PathNames.EARN_DETAILS}
                  element={
                    <ProtectedComponent featureName="rewards">
                      <CapabilityProtectedComponent
                        capabilityCheck={capabilities =>
                          capabilities?.isEarningRewardsCapabilityEnabled
                        }
                      >
                        <EarnDetails />
                      </CapabilityProtectedComponent>
                    </ProtectedComponent>
                  }
                />
              </Route>
            )}

            {/* MANAGE ACCOUNT ROUTES START */}
            <Route element={<PageLayout {...defaultPageLayout} canRoute />}>
              <Route
                path={`${PathNames.SETTINGS}${PathNames.MANAGE_ACCOUNT}`}
                element={<ManageAccount />}
                index
              />
              <Route
                path={`${PathNames.SETTINGS}${PathNames.MANAGE_USER_PROFILE}`}
                element={
                  <Suspense fallback={<RowsSkeletonLoader rows={5} />}>
                    <UserDetailsPage />
                  </Suspense>
                }
              />
              <Route
                path={`${PathNames.SETTINGS}${PathNames.MANAGE_PAYMENT_SUPPORT}`}
                element={<PaymentSupport />}
              />
              {!enableUsLaunchRestrictions && (
                <Route
                  path={`${PathNames.SETTINGS}${PathNames.MANAGE_CHANNELS}`}
                  element={<ManageChannels />}
                />
              )}
              <Route
                path={`${PathNames.SETTINGS}${PathNames.MANAGE_TEAM_MEMBERS}`}
                element={
                  <ProtectedComponent featureName="manageTeamMembers">
                    <TeamMembers />
                  </ProtectedComponent>
                }
              />
              <Route
                path={`${PathNames.SETTINGS}${PathNames.MANAGE_MERCHANT_DETAILS}`}
                element={<MerchantsDetails />}
              />
              <Route
                path={`${PathNames.SETTINGS}${PathNames.LEGAL_AGREEMENTS}`}
                element={
                  <ProfileSettingProtectedPage
                    profileSettingName="isLegalAgreementSectionEnabled"
                    rolePermissionName="manageLegalAgreements"
                    hasOverride={enableLegalAgreements}
                  >
                    <LegalAgreements />
                  </ProfileSettingProtectedPage>
                }
              />
              {enablePaymentLimitsManagement && (
                <Route
                  path={`${PathNames.SETTINGS}${PathNames.PAYMENT_LIMITS}`}
                  element={
                    <ProtectedComponent featureName="paymentLimits">
                      <PaymentLimits />
                    </ProtectedComponent>
                  }
                />
              )}
              {enableBilling && (
                <Route path={`${PathNames.SETTINGS}${PathNames.BILLING}`} element={<Billing />} />
              )}
              {enableHppSettingsPage && (
                <Route
                  path={`${PathNames.SETTINGS}${PathNames.HPP_SETTINGS}`}
                  element={<HppSettings />}
                />
              )}
              <Route
                path={`${PathNames.SETTINGS}${PathNames.COMMUNICATION_PREFERENCES}`}
                element={<CommunicationPreferences />}
              />
            </Route>
            {/* MANAGE ACCOUNT ROUTES END */}
            <Route path={PathNames.LOGIN} element={<LoginNew />} />
            <Route path={PathNames.GET_STARTED} element={<CTAGetStarted />} />
            <Route key="forgot" path={PathNames.FORGOT} element={<ForgotPassword />} />
            <Route
              key="forgot-password"
              path={PathNames.FORGOT_PASSWORD}
              element={<ForgotPassword />}
            />
            <Route key="reset-password" path={PathNames.RESET_TOKEN} element={<PasswordReset />} />
            <Route key="impersonate" path={PathNames.IMPERSONATE} element={<Impersonate />} />
            <Route path={PathNames.LOGOUT} element={<Logout />} />
            <Route
              path={PathNames.AUTHENTICATE}
              element={
                <Authenticate
                  isLoadingData={isLoadingRequestsOnLogin}
                  verificationRequestStatus={verificationRequestStatus}
                />
              }
            />
            <Route
              element={
                <PageLayout
                  {...defaultPageLayout}
                  title={!isComplianceView ? t('home') : t('settingsPaymentSupport')}
                  isTitleEnabled={isComplianceView}
                />
              }
            >
              <Route
                path="/"
                element={
                  <GetStarted
                    isComplianceView={isComplianceView}
                    complianceViewComponent={<PaymentSupport />}
                  />
                }
              />
            </Route>
            <Route path={PathNames.VERIFY} element={<Verify />} />
            <Route path={PathNames.AGREEMENTS} element={<CustomerPendingAgreements />} />
            <Route path={PathNames.VERIFICATION_STATUS} element={<VerificationStatus />} />
            <Route
              path={PathNames.APPLICATION_ONBOARDING}
              element={
                <ApplicationOnboarding verificationRequestStatus={verificationRequestStatus} />
              }
            />
            <Route
              path={PathNames.APPLICATION_UNSUCCESSFUL}
              element={<ApplicationUnsuccessful />}
            />
            <Route path={PathNames.ACCOUNT_ERROR} element={<AccountError />} />
            <Route path="*" element={<NotFound />} />
          </Routes>

          {isVerifiedBusinessAccount && <PendingAgreements />}
        </AppProviders>
        {isLocal && <DevTools />}
      </Sentry.ErrorBoundary>
    </CacheBuster>
  )
}

export default App
